// Library Methods
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import FilterSection, { FilterGroupType } from "./FilterSection/FilterSection";
import { CollapseLeftIcon, CollapseRightIcon } from "src/assets/icons";

// Styles
import { useTheme } from "@mui/material/styles";
import { getSurfaceTokens } from "src/styles/mui/muiStylesHelper";

type FilterSidebarType = {
  hasData: boolean;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  examTypeFilter: FilterGroupType;
  examDateFilter: FilterGroupType;
  examEyeFilter: FilterGroupType;
};

const FilterSidebar = ({
  hasData,
  isCollapsed,
  onToggleCollapse,
  examTypeFilter,
  examDateFilter,
  examEyeFilter,
}: FilterSidebarType) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { surface, mutedText, dividerBorder } = getSurfaceTokens(theme);

  if (isCollapsed) {
    return (
      <Box
        role="button"
        onClick={onToggleCollapse}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          height: "100%",
          width: "100%",
          p: "14px 8px",
          cursor: "pointer",
          backgroundColor: surface,
        }}
      >
        <Box
          sx={{
            color: mutedText,
          }}
        >
          <CollapseRightIcon />
        </Box>
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography
            sx={{
              fontSize: "12px",
              fontWeight: 600,
              writingMode: "vertical-rl",
              transform: "rotate(180deg)",
              whiteSpace: "nowrap",
              userSelect: "none",
              textTransform: "uppercase",
            }}
          >
            {t("Filters")}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        width: "100%",
        overflowY: "auto",
      }}
    >
      <Box
        role="button"
        onClick={onToggleCollapse}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "14px 8px",
          borderBottom: dividerBorder,
          cursor: "pointer",
          backgroundColor: surface,
        }}
      >
        <Typography sx={{ fontWeight: 600, fontSize: "12px", ml: "8px" }}>
          {t("Filters")}
        </Typography>
        <Box
          sx={{
            color: mutedText,
          }}
        >
          <CollapseLeftIcon />
        </Box>
      </Box>

      {hasData ? (
        <>
          <FilterSection title={t("Eye")} {...examEyeFilter} />
          <FilterSection title={t("Exam Type")} {...examTypeFilter} />
          <FilterSection title={t("Exam Date")} {...examDateFilter} />
        </>
      ) : (
        <Box
          sx={{
            display: "flex",
            flex: 1,
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Typography fontSize="14px" textAlign="center">
            {t("No filters found.")}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default FilterSidebar;
